"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Layout } from "antd";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppLayout from "../../../navigation/Layout/Layout";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";
import useAuthSession from "../../../authPatient/hooks/useAuthSession";
import { useTour } from "../../../appointments/hooks/useTour";

// ✅ Import all tour configs
import { editProfileTourSteps } from "./editProfileTourConfig";
import { medicalHistoryTourSteps } from "./medicalHistoryTourConfig";
import { insuranceTourSteps } from "./insuranceTourConfig";

import EditSidebar from "@/Profile/components/ProfileEditPage/EditSidebar/EditSidebar";
import "./ProfileEdit.css";

const { Content } = Layout;

export default function ProfileEditPage() {
  const { t } = useTranslation();

  const { auth, effectiveUserId, effectiveUser } = useAuthSession({ auto: false });
  const userId = effectiveUserId;

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Tour state
  const [shouldStartTour, setShouldStartTour] = useState(false);
  const [tourSteps, setTourSteps] = useState([]);

  // ✅ Message de succès state
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  /** ---- Avatar local ---- */
  const [localAvatar, setLocalAvatar] = useState(null);
  useEffect(
    () => () => {
      if (localAvatar?.startsWith?.("blob:")) URL.revokeObjectURL(localAvatar);
    },
    [localAvatar]
  );

  const handleAvatarChange = useCallback((updatedUserOrPreview, previewImage) => {
    const nextAvatar =
      previewImage ||
      (typeof updatedUserOrPreview === "string" ? updatedUserOrPreview : null);
    if (nextAvatar) setLocalAvatar(nextAvatar);
  }, []);

  /** ---- Navigation ---- */
  const goBack = () => navigate(-1);
  const goToProfile = () => (userId ? navigate(ROUTES.PROFILE(userId)) : navigate("/profile"));

  /** ---- Global save handler ---- */
  const [saveHandler, setSaveHandler] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const registerSave = useCallback((fn) => {
    setSaveHandler(() => fn);
  }, []);

  const handleSave = async () => {
    if (typeof saveHandler !== "function") {
      console.warn("⚠️ Aucun formulaire enregistré pour la sauvegarde");
      return;
    }

    try {
      setIsSaving(true);

      // Appeler la fonction de sauvegarde
      await saveHandler();

      // ✅ Afficher le message de succès
      setSuccessMessage(t("profileEdit.messages.savedSuccess"));
      setShowSuccessMessage(true);

      // Masquer le message après 3 secondes
      setTimeout(() => setShowSuccessMessage(false), 3000);

      // Redirection vers le profil après sauvegarde réussie
      setTimeout(() => {
        goToProfile();
      }, 800);
    } catch (err) {
      console.error("❌ Erreur lors de la sauvegarde:", err);

      // ✅ Afficher le message d'erreur
      setSuccessMessage(t("profileEdit.messages.savedError"));
      setShowSuccessMessage(true);

      setTimeout(() => setShowSuccessMessage(false), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // ✅ Determine which tour to show based on route and tour param
  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const tourParam = searchParams.get("tour");

    if (!hasGuide && !tourParam) return;

    let selectedTourSteps = editProfileTourSteps;

    if (tourParam === "insurance" || pathname.includes("/profile/edit/insurance")) {
      selectedTourSteps = insuranceTourSteps;
    } else if (tourParam === "medical-history" || pathname.includes("/profile/edit/history")) {
      selectedTourSteps = medicalHistoryTourSteps;
    } else if (tourParam === "edit-profile" || pathname.includes("/profile/edit/general")) {
      selectedTourSteps = editProfileTourSteps;
    }

    setTourSteps(selectedTourSteps);

    setTimeout(() => {
      const grid = document.querySelector(".pe-grid");
      const sidebar = document.querySelector(".profile-edit__sidebar");
      const content = document.querySelector(".pe-content1");

      if (grid && sidebar && content) {
        setShouldStartTour(true);
      } else {
        setTimeout(() => setShouldStartTour(true), 1500);
      }
    }, 1000);
  }, [searchParams, pathname]);

  useTour(shouldStartTour, tourSteps);

  return (
    <AppLayout>
      <div className="pe-content">
        <div className="profile">
          <div className="pe-grid">
            {/* ---- Sidebar gauche ---- */}
            <div className="profile-edit__sidebar">
              <EditSidebar
                user={effectiveUser || auth?.user}
                avatarSrc={localAvatar}
                onAvatarChange={handleAvatarChange}
                routes={{
                  general: ROUTES.PROFILE_EDIT_GENERAL,
                  history: "/profile/edit/history",
                  insurance: "/profile/edit/insurance",
                }}
              />
            </div>

            {/* ---- Colonne droite ---- */}
            <div className="profile-edit__main">
              <div className="pe-mainbar">
                <div className="pe-mainbar-left">
                  <button
                    type="button"
                    className="pe-back"
                    onClick={goToProfile}
                    aria-label={t("profileEdit.actions.backAria")}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 19L5 12L12 5"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 12H5"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <button type="button" className="pe-title-link" onClick={goToProfile}>
                    {t("profileEdit.title")}
                  </button>
                </div>
              </div>

              <div className="pe-content1">
                <Outlet context={{ registerSave }} />
              </div>

              {/* ---- Footer ---- */}
              <div className="pe-footer">
                <button type="button" className="pe-discard" onClick={goBack}>
                  {t("profileEdit.actions.discard")}
                </button>

                <button type="button" className="pe-save" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? t("profileEdit.actions.saving") : t("profileEdit.actions.save")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Message overlay de succès/erreur */}
        {showSuccessMessage && <div className="pe-message-overlay">{successMessage}</div>}
      </div>
    </AppLayout>
  );
}
